import { Dispatch, SetStateAction, useContext, useEffect, useMemo } from "react";
import { IConfirmationButton, IConfirmationModalStat } from ".";
import {
  GetAccount200ResponsePositionsInner,
  GetOrderbook200Response,
  GetRfqs200ResponseBlocksInner,
  GetRfqsBlockIdQuotes200ResponseAsksInner,
  InstrumentTypeResponse,
  SideResponse,
} from "../../codegen-api";
import { IMarketPrecisionGetter } from "../../contexts/MarketInstrumentContext";
import { PositionCloseEstimate } from "../../utils/positionClose";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { ClosePositionPageEnum } from "./ClosePositionModal.types";
import { buildCloseStats, buildTags } from "./ClosePositionModal.stats";
import { SettingsContext } from "../../contexts/SettingsContext";
import { worstFillPrice } from "../../utils/amount/slippage";
import {
  buildConfirmationButtons,
  buildPageContent,
} from "./ClosePositionModal.buttons";

type TranslateFn = (...args: any[]) => any;
type AccountData = ReturnType<typeof useGetAccount>["data"];

interface IUseClosePositionPresentationParams {
  show: boolean | undefined;
  canClosePosition: boolean;
  position: GetAccount200ResponsePositionsInner | undefined;
  setError: Dispatch<SetStateAction<string | undefined>>;
  setWarning: Dispatch<SetStateAction<string | undefined>>;
  setPageIndex: Dispatch<SetStateAction<number>>;
  setCanClosePosition: Dispatch<SetStateAction<boolean>>;
  setAvailablePages: Dispatch<SetStateAction<ClosePositionPageEnum[]>>;
  hasQuote: boolean;
  hasRequest: boolean;
  page: ClosePositionPageEnum;
  selectedQuote: GetRfqsBlockIdQuotes200ResponseAsksInner | undefined;
  selectedRfqBlock: GetRfqs200ResponseBlocksInner | undefined;
  t: TranslateFn;
  orderbookData: GetOrderbook200Response | undefined;
  getMarketPrecision: IMarketPrecisionGetter;
  closeEstimate: PositionCloseEstimate | undefined;
  isPerp: boolean;
  positionAmount: number;
  closeSizeInput: string;
  accountData: AccountData;
  closeMode: "market" | "limit";
  executePerpClose: () => void;
  perpCloseValid: boolean;
  onNextPage: () => void;
  onOpenPositionConfirmationModal: () => void;
  onOpenPnLCard: () => void;
  onPrevPage: () => void;
  loading: boolean;
  onAcceptQuote: () => void;
  onCreate: () => void;
}

export const useClosePositionPresentation = ({
  show,
  canClosePosition,
  position,
  setError,
  setWarning,
  setPageIndex,
  setCanClosePosition,
  setAvailablePages,
  hasQuote,
  hasRequest,
  page,
  selectedQuote,
  selectedRfqBlock,
  t,
  orderbookData,
  getMarketPrecision,
  closeEstimate,
  isPerp,
  positionAmount,
  closeSizeInput,
  accountData,
  closeMode,
  executePerpClose,
  perpCloseValid,
  onNextPage,
  onOpenPositionConfirmationModal,
  onOpenPnLCard,
  onPrevPage,
  loading,
  onAcceptQuote,
  onCreate,
}: IUseClosePositionPresentationParams) => {
  // Whenever the modal is presented, reset state.
  useEffect(() => {
    if (!show) {
      setError(undefined);
      setWarning(undefined);
      setPageIndex(0);
    }
  }, [show]);

  useEffect(() => {
    // Only allow options to request RFQ
    if (
      !canClosePosition &&
      position?.instrument_type === InstrumentTypeResponse.Option
    ) {
      setAvailablePages([
        ClosePositionPageEnum.DETAILS,
        ClosePositionPageEnum.REQUEST_RFQ,
      ]);
    } else {
      setAvailablePages([ClosePositionPageEnum.DETAILS]);
    }
  }, [canClosePosition, position?.instrument_type]);

  const pageContent = useMemo(
    () =>
      buildPageContent({
        page,
        position,
        hasQuote,
        hasRequest,
        selectedQuote,
        t,
      }),
    [hasQuote, hasRequest, page, position, selectedQuote, t]
  );

  // Validates available liquidity for market buy/sell
  useEffect(() => {
    if (!position || page === ClosePositionPageEnum.REQUEST_RFQ) {
      return;
    }

    let totalLiquidity: number | undefined;

    if (position.side === SideResponse.Buy) {
      // if long, check bids liquidity (selling into bids)
      const totalBidsSize =
        orderbookData?.bids?.reduce((prev, bid) => {
          const bidSize = Number(bid[1] || 0);
          return prev + bidSize;
        }, 0) || 0;
      totalLiquidity = totalBidsSize;
    } else if (position.side === SideResponse.Sell) {
      // if short, check ask liquidity (buying from asks)
      const totalAsksSize =
        orderbookData?.asks?.reduce((prev, ask) => {
          const askSize = Number(ask[1] || 0);
          return prev + askSize;
        }, 0) || 0;
      totalLiquidity = totalAsksSize;
    }

    // Reset
    setCanClosePosition(true);
    setWarning("");

    if (totalLiquidity !== undefined) {
      setError(undefined);
      if (totalLiquidity === 0) {
        setWarning(t("insufficient_liquidity_error"));
        setCanClosePosition(false);
      } else if (totalLiquidity < parseFloat(position.amount)) {
        setWarning(t("partial_liquidity_warning"));
      }
    }
  }, [orderbookData?.bids, orderbookData?.asks, position, t, page]);

  const { slippageBps } = useContext(SettingsContext);

  // Only a MARKET close carries a price bound, so only a market close states
  // one. Closing a LONG sells into the bid; closing a SHORT buys from the ask.
  const slippage = useMemo(() => {
    if (!isPerp || closeMode !== "market") return undefined;
    const closingLong = position?.side === SideResponse.Buy;
    const levels = closingLong ? orderbookData?.bids : orderbookData?.asks;
    const best = Array.isArray(levels) ? levels[0] : undefined;
    const reference = Array.isArray(best) ? String(best[0]) : null;
    const precision = position
      ? getMarketPrecision(position.asset, position.instrument_type).price_precision
      : 2;
    return {
      bps: slippageBps,
      worstFillText:
        worstFillPrice(reference, slippageBps, !closingLong, precision) ?? undefined,
    };
  }, [
    closeMode,
    getMarketPrecision,
    isPerp,
    orderbookData?.asks,
    orderbookData?.bids,
    position,
    slippageBps,
  ]);

  const stats: IConfirmationModalStat[] = useMemo(
    () =>
      buildCloseStats({
        position,
        page,
        isPerp,
        positionAmount,
        closeSizeInput,
        closeEstimate,
        getMarketPrecision,
        slippage,
        t,
      }),
    [
      position,
      t,
      getMarketPrecision,
      page,
      closeEstimate?.averagePrice,
      closeEstimate?.realizedPnl,
      closeEstimate?.closedEntryValue,
      isPerp,
      positionAmount,
      closeSizeInput,
      slippage,
    ]
  );

  const tags = useMemo(
    () => buildTags({ position, page, t }),
    [page, position, t]
  );

  const confirmationButtons: IConfirmationButton[] = useMemo(
    () =>
      buildConfirmationButtons({
        page,
        canClosePosition,
        position,
        accountData,
        isPerp,
        closeMode,
        executePerpClose,
        perpCloseValid,
        onNextPage,
        onOpenPositionConfirmationModal,
        onOpenPnLCard,
        onPrevPage,
        selectedRfqBlock,
        selectedQuote,
        loading,
        onAcceptQuote,
        onCreate,
        t,
      }),
    [
      page,
      t,
      canClosePosition,
      position?.instrument_type,
      accountData?.in_liquidation,
      onOpenPnLCard,
      onNextPage,
      onOpenPositionConfirmationModal,
      onPrevPage,
      selectedRfqBlock,
      selectedQuote,
      loading,
      onAcceptQuote,
      onCreate,
      isPerp,
      closeMode,
      executePerpClose,
      perpCloseValid,
    ]
  );

  return {
    pageContent,
    stats,
    tags,
    confirmationButtons,
  };
};
